/**
 * AsyncSearchableSelect - A typeahead dropdown that fetches options from the
 * server as the user types. Sister component to SearchableSelect, which is
 * client-side filter only.
 *
 * Use this when the underlying option list is too large to load eagerly
 * (e.g., user lists in tenants with hundreds or thousands of users). The
 * parent supplies an async fetchOptions callback; the component debounces
 * keystrokes (default 300ms), shows a loading state, and renders results.
 *
 * Because the option list is dynamic, the component does not know the label
 * for an already-selected value. Pass `selectedLabel` so the input can
 * display "John Smith" instead of the raw ID when the dropdown is closed.
 *
 * @example
 * <AsyncSearchableSelect
 *   id="assigneeId"
 *   value={assigneeId}
 *   selectedLabel={selectedTechName}
 *   onChange={(id, opt) => { setAssigneeId(id); cacheTech(opt); }}
 *   fetchOptions={(search) =>
 *     api.get('/users', { role: 'technician', search, per_page: '25' })
 *       .then((res) => (res.data.data ?? res.data).map((u) => ({
 *         value: String(u.id), label: `${u.first_name} ${u.last_name}`,
 *       })))
 *   }
 * />
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { DropdownFooterAction } from '../../atoms/DropdownFooterAction';
import type { DropdownFooterActionConfig } from '../../atoms/DropdownFooterAction';

export interface AsyncSearchableSelectOption {
	value: string;
	label: string;
	/** Optional muted second line of context rendered under the label in the
	 *  dropdown (e.g. "Technician · 3 open jobs"). Not shown in the closed
	 *  input — that still uses `label` / `selectedLabel`. */
	sublabel?: string;
}

export interface AsyncSearchableSelectProps {
	/** Unique id for the input element */
	id: string;
	/** Currently selected value (empty string = no selection) */
	value: string;
	/** Called with the new value and the chosen option (or null when cleared) */
	onChange: (value: string, option: AsyncSearchableSelectOption | null) => void;
	/** Async loader. Called with the (possibly empty) search text after debouncing. */
	fetchOptions: (search: string) => Promise<AsyncSearchableSelectOption[]>;
	/** Label to display for the currently-selected value when the dropdown is closed.
	 *  Required when `value` is set, otherwise the input shows the raw id. */
	selectedLabel?: string;
	/** Placeholder when no value is selected */
	placeholder?: string;
	/** Additional CSS classes applied to the wrapper div */
	className?: string;
	/** Whether the input is disabled */
	disabled?: boolean;
	/** Error state — adds red border */
	hasError?: boolean;
	/** Debounce delay in ms before firing fetchOptions on keystrokes (default 300) */
	debounceMs?: number;
	/**
	 * Message shown when a search has completed with no matches. Defaults to a
	 * generic "No results found"; pass something specific ("No people in this
	 * company yet") so an empty list explains itself.
	 */
	emptyMessage?: string;
	/**
	 * Message shown when the lookup itself failed. Kept separate from
	 * `emptyMessage` because the two say opposite things: "there is nobody"
	 * sends the user off to create records that already exist, while "we
	 * couldn't ask" tells them to retry. Defaults to a generic retry prompt.
	 */
	errorMessage?: string;
	/**
	 * Optional call-to-action row pinned under the list (e.g. "+ Quick Add
	 * User"). Always shown while the list is open — including when the search
	 * found nobody, which is when it matters most. Receives the search text.
	 */
	footerAction?: DropdownFooterActionConfig;
}

const AsyncSearchableSelect: React.FC<AsyncSearchableSelectProps> = ({
	id,
	value,
	onChange,
	fetchOptions,
	selectedLabel = '',
	placeholder = 'Search...',
	className = '',
	disabled = false,
	hasError = false,
	debounceMs = 300,
	emptyMessage = 'No results found',
	errorMessage = "Couldn't load results. Check your connection and try again.",
	footerAction,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [options, setOptions] = useState<AsyncSearchableSelectOption[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	/* Whether a fetch has completed since the dropdown opened. Without this an
	   empty result set was indistinguishable from "haven't looked yet", so a
	   genuinely empty list told the user to "Type to search..." forever. */
	const [hasFetched, setHasFetched] = useState(false);
	/* Whether the last fetch threw. A failed lookup used to fall through to the
	   empty-result branch, so a 500 or a dropped connection rendered as "No
	   people found. Add them under Admin → Users." — the dropdown blaming the
	   data for a problem with the request, and sending the user off to create
	   people who were already there. */
	const [loadFailed, setLoadFailed] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	/* Tracks the latest fetch so stale responses (slow request returning after a
	   newer fast one) don't overwrite the displayed list. */
	const fetchSeqRef = useRef(0);

	/* Load options for the current search text. Increments the sequence so any
	   in-flight earlier request is ignored when it eventually resolves. */
	const loadOptions = useCallback(async (search: string) => {
		const seq = ++fetchSeqRef.current;
		setIsLoading(true);
		try {
			const results = await fetchOptions(search);
			if (seq === fetchSeqRef.current) {
				setOptions(results);
				setLoadFailed(false);
			}
		} catch (err) {
			if (seq === fetchSeqRef.current) {
				setOptions([]);
				setLoadFailed(true);
			}
		} finally {
			if (seq === fetchSeqRef.current) {
				setIsLoading(false);
				setHasFetched(true);
			}
		}
	}, [fetchOptions]);

	/* Debounce searchText changes while the dropdown is open. Empty search runs
	   immediately on open so the user sees results without typing. */
	useEffect(() => {
		if (!isOpen) return;

		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}

		const delay = searchText === '' ? 0 : debounceMs;
		debounceTimerRef.current = setTimeout(() => {
			loadOptions(searchText);
		}, delay);

		return () => {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, [searchText, isOpen, debounceMs, loadOptions]);

	/* Close dropdown when clicking outside */
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
				setIsOpen(false);
				setSearchText('');
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleSelect = (opt: AsyncSearchableSelectOption) => {
		onChange(opt.value, opt);
		setIsOpen(false);
		setSearchText('');
	};

	const handleClear = () => {
		onChange('', null);
		setSearchText('');
		inputRef.current?.focus();
	};

	/* When the dropdown is closed, display the selected label (or fall back to
	   the value if the parent didn't supply one). When open, show the search text. */
	const displayValue = isOpen ? searchText : (value ? (selectedLabel || value) : '');

	/* Open the list with a fresh lookup. Focus used to be the only way in,
	   but picking an option keeps focus in the input (the option's mousedown
	   is prevented), so the next click or keystroke landed on an input that
	   was already focused and the list never came back. "+ Add assignee" hit
	   this straight away: pick the first person, go to add a second, nothing. */
	const openList = (search = '') => {
		if (disabled) return;
		setIsOpen(true);
		setSearchText(search);
		setHasFetched(false);
		setLoadFailed(false);
	};

	const handleClick = () => {
		if (!isOpen) openList();
	};

	/* Typing into a closed list reopens it. The search starts from what was
	   just typed, not from the selected label the input was showing — typing
	   "L" after picking Carlos Martinez searches "L". */
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const typed = e.target.value;
		if (isOpen) {
			setSearchText(typed);
			return;
		}
		openList(typed.startsWith(displayValue) ? typed.slice(displayValue.length) : typed);
	};

	const borderClass = hasError
		? 'border-red-500 focus:ring-red-500'
		: 'border-brand-border focus:border-brand-primary focus:ring-brand-primary';

	return (
		<div ref={wrapperRef} className={`async-searchable-select relative ${className}`} data-ref="molecule-async-searchable-select">
			<div className="async-searchable-select__control relative">
				<input
					ref={inputRef}
					id={id}
					type="text"
					value={displayValue}
					onChange={handleInputChange}
					onFocus={() => openList()}
					onClick={handleClick}
					disabled={disabled}
					placeholder={isOpen ? 'Type to search...' : placeholder}
					/* Room on the right for whichever control sits there: the
					   clear button's tap box is 44px wide (plus its 8px inset),
					   so a long name stops before it rather than running under
					   the ×; the chevron needs less. */
					className={`w-full px-12px py-8px ${value && !disabled ? 'pr-52px' : 'pr-32px'} min-h-44px desktop:min-h-36px text-16px font-sans rounded-md border ${borderClass} outline-none focus:ring-2 focus:ring-opacity-20 disabled:opacity-50 disabled:cursor-not-allowed bg-white`}
					/* The whole name on hover, for one too long for the box. */
					title={value ? displayValue : undefined}
					autoComplete="off"
				/>
				{value && !disabled && (
					<button type="button" onClick={handleClear} className="absolute right-8px top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 min-w-44px min-h-44px flex items-center justify-center" aria-label="Clear selection">
						<svg className="w-16px h-16px" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
					</button>
				)}
				{!value && (
					<svg className="absolute right-12px top-1/2 -translate-y-1/2 w-16px h-16px text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
				)}
			</div>

			{/* The panel holds the scrolling list plus the optional footer
			    action, which stays put below the scroll area. */}
			{isOpen && (
				<div className="async-searchable-select__panel absolute z-50 mt-4px w-full bg-white border border-brand-border rounded-md shadow-lg overflow-hidden">
				<ul className="async-searchable-select__list max-h-224px overflow-y-auto" role="listbox">
					{isLoading ? (
						<li className="px-12px py-8px text-14px text-brand-text-muted font-sans flex items-center gap-8px">
							<span className="inline-block w-12px h-12px border-2 border-gray-300 border-t-brand-primary rounded-full animate-spin" />
							Searching...
						</li>
					) : loadFailed ? (
						<li role="alert" className="async-searchable-select__error px-12px py-8px text-14px text-red-600 font-sans">
							{/* The lookup failed — say so rather than reporting an
							    empty list, which reads as a fact about the data. */}
							{errorMessage}
						</li>
					) : options.length === 0 ? (
						<li className="async-searchable-select__empty px-12px py-8px text-14px text-brand-text-muted font-sans">
							{/* Once a fetch has actually come back empty, say so.
							    "Type to search..." is only honest before the first
							    request resolves. */}
							{hasFetched ? emptyMessage : 'Type to search...'}
						</li>
					) : (
						options.map((opt) => (
							<li
								key={opt.value}
								role="option"
								aria-selected={opt.value === value}
								onMouseDown={(e) => {
									/* Use mousedown so the click registers before the input's blur
									   triggers the click-outside handler that closes the dropdown. */
									e.preventDefault();
									handleSelect(opt);
								}}
								className={`async-searchable-select__option${opt.value === value ? ' async-searchable-select__option--selected' : ''} px-12px py-8px text-14px font-sans cursor-pointer min-h-44px flex flex-col justify-center transition-colors ${opt.value === value ? 'bg-brand-primary text-white' : 'text-brand-text hover:bg-brand-surface'}`}
							>
								<span className="async-searchable-select__option-label">{opt.label}</span>
								{opt.sublabel && (
									<span className={`async-searchable-select__option-sublabel text-12px ${opt.value === value ? 'text-white/70' : 'text-brand-text-muted'}`}>
										{opt.sublabel}
									</span>
								)}
							</li>
						))
					)}
				</ul>
				{footerAction && (
					<DropdownFooterAction
						label={footerAction.label}
						onSelect={() => {
							const text = searchText;
							setIsOpen(false);
							setSearchText('');
							inputRef.current?.blur();
							footerAction.onSelect(text);
						}}
					/>
				)}
				</div>
			)}
		</div>
	);
};

export default AsyncSearchableSelect;
